import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Select } from '../ui/Select';
import { Input } from '../ui/Input';
import { Switch } from '../ui/Switch';
import { Stack } from '../layout/Stack';
import { smartNotificationService } from '@/services/intelligentScheduling/SmartNotificationService';
import { 
  TestTube, 
  Bell, 
  Play, 
  Pause, 
  RotateCcw, 
  Settings,
  Clock,
  AlertCircle,
  Target,
  Calendar,
  Zap,
  Volume2,
  Vibrate
} from 'lucide-react';

interface MockNotificationConfig {
  title: string;
  message: string;
  type: 'schedule_reminder' | 'deadline_warning' | 'task_suggestion' | 'break_reminder';
  priority: 'low' | 'medium' | 'high' | 'critical';
  contextFactors: string[];
  confidence: number;
  sound: boolean;
  vibration: boolean;
  showPreview: boolean;
}

const defaultConfig: MockNotificationConfig = {
  title: '🎯 Time to work on your goals!',
  message: 'Based on your schedule, this is an optimal time to focus on your important tasks.',
  type: 'schedule_reminder',
  priority: 'medium',
  contextFactors: ['peak_productivity', 'available_time', 'low_distractions'],
  confidence: 0.85,
  sound: true,
  vibration: true,
  showPreview: true
};

const sampleNotifications = [
  {
    title: '⏰ Scheduled Task Reminder',
    message: 'Your "Weekly Planning" session is starting in 15 minutes.',
    type: 'schedule_reminder' as const,
    priority: 'medium' as const,
    contextFactors: ['scheduled_time', 'calendar_available']
  },
  {
    title: '🚨 Deadline Approaching',
    message: 'Your "Project Proposal" is due in 2 hours. Time to focus!',
    type: 'deadline_warning' as const,
    priority: 'high' as const,
    contextFactors: ['deadline_urgency', 'high_priority']
  },
  {
    title: '💡 Smart Suggestion',
    message: 'Perfect time for deep work! Your energy is high and distractions are low.',
    type: 'task_suggestion' as const,
    priority: 'medium' as const,
    contextFactors: ['energy_peak', 'focus_opportunity', 'calendar_free']
  },
  {
    title: '🌱 Take a Break',
    message: 'You\'ve been focused for 90 minutes. A 10-minute break will boost productivity!',
    type: 'break_reminder' as const,
    priority: 'low' as const,
    contextFactors: ['long_focus_session', 'productivity_optimization']
  },
  {
    title: '🎉 Achievement Unlocked',
    message: 'Congratulations! You\'ve completed 5 goals this week. Keep up the momentum!',
    type: 'task_suggestion' as const,
    priority: 'low' as const,
    contextFactors: ['achievement_milestone', 'positive_reinforcement']
  }
];

export function MockNotificationDisplay() {
  const [config, setConfig] = useState<MockNotificationConfig>(defaultConfig);
  const [isLivePreview, setIsLivePreview] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const updateConfig = (updates: Partial<MockNotificationConfig>) => {
    setConfig(prev => ({ ...prev, ...updates }));
  };

  const loadSampleNotification = (sample: typeof sampleNotifications[0]) => {
    setConfig(prev => ({
      ...prev,
      title: sample.title,
      message: sample.message,
      type: sample.type,
      priority: sample.priority,
      contextFactors: sample.contextFactors,
      confidence: 0.8
    }));
  };

  const sendTestNotification = async () => {
    try {
      await smartNotificationService.sendScheduleNotification(
        `test-${Date.now()}`,
        'test-list',
        config.title,
        config.message,
        'schedule_reminder',
        {
          priority: config.priority,
          confidence: config.confidence,
          contextFactors: config.contextFactors,
        }
      );

      // Also send browser notification if permission granted
      if (Notification.permission === 'granted') {
        const notification = new Notification(config.title, {
          body: config.message,
          icon: '/favicon.ico',
          badge: '/favicon.ico',
          tag: 'test-notification',
          requireInteraction: config.priority === 'high' || config.priority === 'critical',
          silent: !config.sound,
        });

        if (config.vibration && 'vibrate' in navigator) {
          navigator.vibrate([200, 100, 200]);
        }

        // Auto-close after 5 seconds for testing
        setTimeout(() => {
          notification.close();
        }, 5000);
      }
    } catch (error) {
      console.error('Failed to send test notification:', error);
    }
  };

  // Clean up interval when component unmounts
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  const startLivePreview = () => {
    // Prevent multiple intervals
    if (intervalRef.current) return;

    setIsLivePreview(true);
    let sampleIndex = 0;

    // Send first notification immediately
    const sample = sampleNotifications[sampleIndex];
    loadSampleNotification(sample);
    sendTestNotification();
    sampleIndex = (sampleIndex + 1) % sampleNotifications.length;

    // Then set up interval for subsequent notifications
    intervalRef.current = setInterval(() => {
      const sample = sampleNotifications[sampleIndex];
      loadSampleNotification(sample);
      sendTestNotification();
      
      sampleIndex = (sampleIndex + 1) % sampleNotifications.length;
    }, 10000); // Every 10 seconds
  };

  const stopLivePreview = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsLivePreview(false);
  };

  const resetToDefault = () => {
    stopLivePreview();
    setConfig(defaultConfig);
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'schedule_reminder':
        return <Clock className="h-4 w-4" />;
      case 'deadline_warning':
        return <AlertCircle className="h-4 w-4" />;
      case 'task_suggestion':
        return <Target className="h-4 w-4" />;
      case 'break_reminder':
        return <Calendar className="h-4 w-4" />;
      default:
        return <Bell className="h-4 w-4" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical':
        return 'border-red-500 bg-red-50 dark:bg-red-950/20';
      case 'high':
        return 'border-orange-500 bg-orange-50 dark:bg-orange-950/20';
      case 'medium':
        return 'border-blue-500 bg-blue-50 dark:bg-blue-950/20';
      default:
        return 'border-gray-500 bg-gray-50 dark:bg-gray-950/20';
    }
  };

  return (
    <div className="space-y-6">
      {/* Live Preview Controls */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <TestTube className="h-5 w-5 mr-2" />
            Notification Testing Lab
          </CardTitle>
          <CardDescription>
            Test and preview how notifications will look and behave with different settings
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button onClick={sendTestNotification} className="flex-1 sm:flex-none">
              <Bell className="h-4 w-4 mr-2" />
              Send Test
            </Button>

            {!isLivePreview ? (
              <Button variant="outline" onClick={startLivePreview} className="flex-1 sm:flex-none">
                <Play className="h-4 w-4 mr-2" />
                Start Live Preview
              </Button>
            ) : (
              <Button variant="outline" onClick={stopLivePreview} className="flex-1 sm:flex-none">
                <Pause className="h-4 w-4 mr-2" />
                Stop Live Preview
              </Button>
            )}

            <Button variant="ghost" onClick={resetToDefault} className="flex-1 sm:flex-none">
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset
            </Button>
          </div>

          {isLivePreview && (
            <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <div className="flex items-center">
                <Zap className="h-4 w-4 mr-2 text-blue-600 animate-pulse" />
                <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
                  Live preview active - sending sample notifications every 10 seconds
                </span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Configuration Panel */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Settings className="h-5 w-5 mr-2" />
              Configuration
            </CardTitle>
            <CardDescription>
              Customize the notification content and behavior
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Stack spacing="md">
              <div>
                <label className="text-sm font-medium mb-2 block">Title</label>
                <Input
                  value={config.title}
                  onChange={(e) => updateConfig({ title: e.target.value })}
                  placeholder="Notification title"
                  disabled={isLivePreview}
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Message</label>
                <textarea
                  value={config.message}
                  onChange={(e) => updateConfig({ message: e.target.value })}
                  placeholder="Notification message"
                  className="w-full p-2 border rounded-md bg-background resize-none"
                  rows={3}
                  disabled={isLivePreview}
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Type</label>
                <Select
                  value={config.type}
                  onValueChange={(value) => updateConfig({ type: value as typeof config.type })}
                  disabled={isLivePreview}
                >
                  <option value="schedule_reminder">Schedule Reminder</option>
                  <option value="deadline_warning">Deadline Warning</option>
                  <option value="task_suggestion">Task Suggestion</option>
                  <option value="break_reminder">Break Reminder</option>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Priority</label>
                <Select
                  value={config.priority}
                  onValueChange={(value) => updateConfig({ priority: value as typeof config.priority })}
                  disabled={isLivePreview}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">
                  AI Confidence: {Math.round(config.confidence * 100)}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={config.confidence}
                  onChange={(e) => updateConfig({ confidence: parseFloat(e.target.value) })}
                  className="w-full"
                  disabled={isLivePreview}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Volume2 className="h-4 w-4 mr-2" />
                  <span className="text-sm">Sound</span>
                </div>
                <Switch
                  checked={config.sound}
                  onCheckedChange={(sound) => updateConfig({ sound })}
                  disabled={isLivePreview}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Vibrate className="h-4 w-4 mr-2" />
                  <span className="text-sm">Vibration</span>
                </div>
                <Switch
                  checked={config.vibration}
                  onCheckedChange={(vibration) => updateConfig({ vibration })}
                  disabled={isLivePreview}
                />
              </div>
            </Stack>
          </CardContent>
        </Card>

        {/* Preview Panel */}
        <div className="space-y-4">
          {/* Browser Notification Preview */}
          <Card>
            <CardHeader>
              <CardTitle>Browser Notification Preview</CardTitle>
              <CardDescription>How it will appear in your browser</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="p-3 border rounded-lg bg-background dark:bg-gray-900 shadow-sm">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-blue-500 rounded flex items-center justify-center flex-shrink-0">
                    <Bell className="h-4 w-4 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {config.title}
                    </h4>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                      {config.message}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                      GodMode • now
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* In-App Notification Preview */}
          <Card>
            <CardHeader>
              <CardTitle>In-App Notification Preview</CardTitle>
              <CardDescription>How it will appear in the notification center</CardDescription>
            </CardHeader>
            <CardContent>
              <div className={`border rounded-lg p-4 transition-all ${getPriorityColor(config.priority)}`}>
                <div className="flex items-start space-x-3">
                  <div className="p-2 rounded-full bg-background dark:bg-gray-800 flex-shrink-0">
                    {getTypeIcon(config.type)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-medium truncate">
                        {config.title}
                      </h3>
                      <div className="flex items-center space-x-2">
                        <Badge variant="outline" className="text-xs">
                          {Math.round(config.confidence * 100)}%
                        </Badge>
                        <Badge 
                          variant={config.priority === 'critical' || config.priority === 'high' ? 'destructive' : 'secondary'}
                          className="text-xs"
                        >
                          {config.priority}
                        </Badge>
                      </div>
                    </div>
                    
                    <p className="text-sm text-muted-foreground mb-2">
                      {config.message}
                    </p>
                    
                    <div className="flex items-center text-xs text-muted-foreground">
                      <Clock className="h-3 w-3 mr-1" />
                      Just now
                    </div>

                    {/* Context factors */}
                    <div className="mt-2 flex flex-wrap gap-1">
                      {config.contextFactors.map(factor => (
                        <Badge key={factor} variant="outline" className="text-xs">
                          {factor.replace(/_/g, ' ')}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Sample Notifications */}
          <Card>
            <CardHeader>
              <CardTitle>Sample Notifications</CardTitle>
              <CardDescription>Try these pre-built notification examples</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {sampleNotifications.map((sample, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    size="sm"
                    onClick={() => loadSampleNotification(sample)}
                    className="w-full justify-start text-left h-auto p-3"
                    disabled={isLivePreview}
                  >
                    <div className="flex items-center space-x-2">
                      {getTypeIcon(sample.type)}
                      <div className="text-left">
                        <div className="text-xs font-medium">{sample.title}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          {sample.message}
                        </div>
                      </div>
                    </div>
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}